import { log as logProviderRendered } from "@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNProviderRenderedV2";
import useJSTBroadcast from "@indeed/tare/broadcast/JSTBroadcast";
import { useJSTEventLog } from "@indeed/tare/eventLog/JSTEventLog";
import React, { Suspense, useEffect, useLayoutEffect } from "react";

import { ThemeProvider } from "../../../mads/ThemeProvider";
import { globalErrorHandler } from "../../../viewjob/src/util/globalErrorHandler";
import { subscribeToJobInteractionsLogout } from "../../apollo/globalState/jobInteractionsLifecycle";
import { syncJobInteractionsAccount } from "../../apollo/globalState/jobInteractionsState";
import { getJSTReporter } from "../../error-reporting/JSTReporter";
import { getErrorMessage } from "../../logging/DatadogErrorUtils";
import { DatadogLogger } from "../../logging/DatadogLogger";
import { getDeviceProperties } from "../../logging/LoggableDeviceProperties";
import { RenderErrorBoundary } from "../../logging/RenderErrorBoundary";
import { BridgeProvider } from "../../native-modules/BridgeContext";
import { NativeContextProvider } from "../../props/NativeContext";
import type { NativeContextRNProps } from "../../props/NativeContextRNProps";
import { CodePushDialog } from "../CodePushDialog";
import { LiquidGlassTabBarProvider } from "../liquidGlass";
import { ReportErrorProvider } from "../ReportErrorProvider";

export interface ProviderRootProps {
  nativeContext?: NativeContextRNProps;
}

function getColorMode(nativeContext: NativeContextRNProps | undefined): "dark" | "light" {
  return nativeContext?.userInterfaceStyle === "dark" ? "dark" : "light";
}

/**
 * Wraps an RN root component with shared provider/tracking behavior.
 *
 * @param WrappedComponent The RN root to wrap.
 * @param providerId Identifier for the provider; used for Datadog views and provider-rendered signals.
 * @param screenName JSMA screen name to log on mount. Defaults to `providerId`. Pass `null` to skip
 *   the auto screen-view (e.g. when the root fires its own screen-view with richer extras).
 */
function withProviderTracking<P>(
  WrappedComponent: React.ComponentType<P & ProviderRootProps>,
  providerId: string,
  screenName: string | null = providerId,
) {
  const ProviderRootComponent = (props: P & ProviderRootProps) => {
    // Opt out of React Compiler: this is an HoC-produced component that also
    // reads/calls into shared mutable state during render (globalErrorHandler
    // below). It wraps every provider root, so a miscompile here breaks all
    // providers. (RN-5198 global rollout)
    "use no memo";

    const eventLog = useJSTEventLog();
    const broadcast = useJSTBroadcast();

    useLayoutEffect(() => {
      syncJobInteractionsAccount(props.nativeContext?.accountId);
    }, [props.nativeContext?.accountId]);

    useEffect(() => subscribeToJobInteractionsLogout(broadcast), [broadcast]);

    useEffect(() => {
      logProviderRendered({
        provider: providerId,
        deviceProperties: getDeviceProperties(),
      });

      void DatadogLogger.startView(providerId);
      if (screenName !== null) {
        eventLog.logScreenView(screenName).catch((error: unknown) => {
          getJSTReporter(providerId).warn(`logScreenView failed: ${getErrorMessage(error)}`, {
            containsPII: true,
          });
        });
      }
      return () => {
        void DatadogLogger.stopView(providerId);
      };
    }, [eventLog]);

    globalErrorHandler.addProviderId(providerId);

    return (
      <NativeContextProvider nativeContext={props.nativeContext}>
        <ThemeProvider colorMode={getColorMode(props.nativeContext)}>
          <RenderErrorBoundary provider={providerId}>
            <ReportErrorProvider providerId={providerId}>
              <LiquidGlassTabBarProvider enabled={props.nativeContext?.liquidGlassTabBar}>
                <Suspense fallback={null}>
                  <WrappedComponent {...props} />
                </Suspense>
              </LiquidGlassTabBarProvider>
              <CodePushDialog />
            </ReportErrorProvider>
          </RenderErrorBoundary>
        </ThemeProvider>
      </NativeContextProvider>
    );
  };

  ProviderRootComponent.displayName = `WithProviderTracking(${WrappedComponent.displayName || WrappedComponent.name})`;

  const BridgeProviderRoot = (props: P & ProviderRootProps) => (
    <BridgeProvider>
      <ProviderRootComponent {...props} />
    </BridgeProvider>
  );
  BridgeProviderRoot.displayName = ProviderRootComponent.displayName;

  return BridgeProviderRoot;
}

export default withProviderTracking;
